import {useState, useEffect, useCallback, useContext} from "react";
import Color from "color";
import {resolveVariant} from "../ContentStylesHelper";
import {ClassStylesEditorContext} from "../ContentStylesEditor";

const buildColoursConfig = (registry, activeVariant = null) => {
    const config = {};
    Object.entries(registry).forEach(([key, item]) => {
        if (!item.cssVar) return;
        const normalizeNames = (val) => Array.isArray(val) ? val : [val];

        if (activeVariant !== null) {
            config[key] = {
                name: normalizeNames(resolveVariant(item.cssVar, activeVariant)),
                mainColour: item.mainColour || false,
                autoGenerated: item.autoGenerated || false
            };
        } else if (item.totalVariants) {
            for (let i = 1; i <= item.totalVariants; i++) {
                const variantKey = resolveVariant(key, i);
                config[variantKey] = {
                    name: normalizeNames(resolveVariant(item.cssVar, i)),
                    mainColour: item.mainColour || false,
                    autoGenerated: item.autoGenerated || false
                };
            }
        } else {
            config[key] = {
                name: normalizeNames(item.cssVar),
                mainColour: item.mainColour || false,
                autoGenerated: item.autoGenerated || false
            };
        }
    });
    return config;
};

const manageColours = (editType, coloursConfig, generateColours, updateStylesCallback) => {
    const [colours, setColours] = useState(coloursConfig);

    const {styles: contextStyles} = useContext(ClassStylesEditorContext);

    useEffect(() => {
        setColours(coloursConfig);
    }, [coloursConfig]);

    useEffect(() => {
        const mainKey = Object.keys(coloursConfig).find(k => coloursConfig[k]?.mainColour);
        if (!mainKey) return;

        const mainVarName = coloursConfig[mainKey].name[0];
        // Lookup in Context: Nested -> Flat
        const mainVal = contextStyles[editType]?.[mainVarName] || contextStyles[mainVarName];

        if (mainVal) {
            try {
                const generated = generateColours(Color(mainVal));
                let hasChange = false;
                const nextColours = { ...colours };

                Object.entries(generated).forEach(([key, genColor]) => {
                    if (!nextColours[key]?.autoGenerated) return;

                    const targetVarName = nextColours[key].name[0];
                    const currentVal = contextStyles[editType]?.[targetVarName] || contextStyles[targetVarName];

                    if (currentVal) {
                        const isAuto = Color(currentVal).hex() === genColor.hex();
                        if (nextColours[key].autoGenerated !== isAuto) {
                            nextColours[key] = { ...nextColours[key], autoGenerated: isAuto };
                            hasChange = true;
                        }
                    }
                });

                if (hasChange) setColours(nextColours);
            } catch (e) { console.warn("Auto-colour sync failed", e); }
        }
    }, [coloursConfig, generateColours, contextStyles, editType]);

    const updateColours = useCallback((type, newStyles, isFinal = false) => {
        const colourConfig = colours[type];
        if (!colourConfig) return;

        if (colourConfig.mainColour) {
            const nameKey = Array.isArray(colourConfig.name) ? colourConfig.name[0] : colourConfig.name;
            const mainColourValue = newStyles[nameKey];

            mainColourUpdate(mainColourValue, newStyles, isFinal);
        } else {
            if (colourConfig.autoGenerated) {
                setColours(prevColours => ({
                    ...prevColours,
                    [type]: {...prevColours[type], autoGenerated: false}
                }));
            }
            updateStylesCallback(newStyles, isFinal);
        }
    }, [colours, updateStylesCallback]);

    const mainColourUpdate = useCallback((mainColour, newStyles, isFinal = false) => {
        const generated = generateColours(Color(mainColour));
        let genColours = {};

        Object.entries(colours).forEach(([key, colourProp]) => {
            if (colourProp?.autoGenerated && generated[key]) {
                const hex = generated[key].hex();
                const names = Array.isArray(colourProp.name) ? colourProp.name : [colourProp.name];

                names.forEach(n => {
                    genColours[n] = n.endsWith('-RGB') ? generated[key].rgb().array() : hex;
                });
            }
        });
        updateStylesCallback({...newStyles, ...genColours}, isFinal);
    }, [colours, generateColours, updateStylesCallback]);

    const toggleGeneratedColours = (checked) => {
        setColours(prevColours => {
            const updatedColours = {...prevColours};
            Object.entries(prevColours).forEach(([key, colour]) => {
                if (Object.hasOwn(colour, 'autoGenerated')) {
                    updatedColours[key] = {
                        ...colour,
                        autoGenerated: checked
                    };
                }
            });
            return updatedColours;
        });
    }

    return {colours, updateColours, toggleGeneratedColours };
}

const getDefaultColour = (name, styles) => {
    if (styles && name in styles) return styles[name];

    const element = document.querySelector('.cont-base');
    if (!element) return '#000000';

    const val = getComputedStyle(element).getPropertyValue(name);
    return val ? val.trim() : '#000000';
};

const getColourOnColour = (colour) => {
    const colourRGB = Color(colour).rgb().color;
    const luminance = calculateRelativeLuminance(colourRGB[0], colourRGB[1], colourRGB[2]);
    const isDark = luminance <= 0.5;
    return isDark ? Color('rgba(255, 255, 255, 1)') : Color('rgba(0, 0, 0, .87)');
}

const getRGBFromRGBA = (colour, a) => {
    const [r, g, b] = colour.rgb().color;
    const alpha = a;
    const white = 255;
    const red = Math.round((1 - alpha) * white + alpha * r);
    const green = Math.round((1 - alpha) * white + alpha * g);
    const blue = Math.round((1 - alpha) * white + alpha * b);
    return Color.rgb(red, green, blue);
}

const ensureColourContrast = (colour1, colour2, depth = 0) => {
    if (depth > 10) return colour1;

    const [r1, g1, b1] = Color(colour1).rgb().color;
    const luminance1 = calculateRelativeLuminance(r1, g1, b1);

    const [r2, g2, b2] = Color(colour2).rgb().color;
    const luminance2 = calculateRelativeLuminance(r2, g2, b2);

    const contrastRatio = calculateContrastRatio(luminance1, luminance2);

    if (contrastRatio < 1.8) {
        let [h, s, l] = Color(colour1).hsl().color;

        s *= 1.2;
        s = Math.max(0, s);
        s = Math.min(100, s);

        l *= 0.8;
        l = Math.max(0, l);
        l = Math.min(100, l);
        // Recheck
        return ensureColourContrast(Color.hsl(h, s, l), colour2, depth + 1);
    } else {
        return colour1;
    }
}

const calculateRelativeLuminance = (r, g, b) => {
    const rsrgb = r / 255;
    const gsrgb = g / 255;
    const bsrgb = b / 255;

    const rlinear = rsrgb <= 0.03928 ? rsrgb / 12.92 : Math.pow((rsrgb + 0.055) / 1.055, 2.4);
    const glinear = gsrgb <= 0.03928 ? gsrgb / 12.92 : Math.pow((gsrgb + 0.055) / 1.055, 2.4);
    const blinear = bsrgb <= 0.03928 ? bsrgb / 12.92 : Math.pow((bsrgb + 0.055) / 1.055, 2.4);

    return 0.2126 * rlinear + 0.7152 * glinear + 0.0722 * blinear;
}

const calculateContrastRatio = (luminance1, luminance2) => {
    const lighter = Math.max(luminance1, luminance2);
    const darker = Math.min(luminance1, luminance2);

    return (lighter + 0.05) / (darker + 0.05);
}

export {
    buildColoursConfig,
    manageColours,
    getDefaultColour,
    getColourOnColour,
    getRGBFromRGBA,
    ensureColourContrast,
    calculateRelativeLuminance,
    calculateContrastRatio
}